import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl } from '../utils/apiUtils';

const EditModuleWorking = () => {
  const { currentUser, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState('');
  const [moduleData, setModuleData] = useState({
    module_code: '',
    module_name: '',
    credits: '',
    description: '',
    semester: '',
    photo_url: '',
    active: true
  });

  // Load modules
  useEffect(() => {
    const loadModules = async () => {
      if (!currentUser || !token) {
        console.log('No authenticated user or token found');
        return;
      }

      try {
        setLoading(true);
        console.log('Loading modules for user:', currentUser);
        console.log('Using token:', token ? 'Token available' : 'No token');
        
        const response = await fetch(`${getApiUrl()}/modules`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        console.log('Response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('Modules loaded:', data?.length || 0, data);
          console.log('Active modules received:', data?.filter(m => m.active !== false).length);
          setModules(data || []);
        } else {
          console.error('Failed to load modules:', response.status);
          const errorData = await response.text();
          console.error('Error details:', errorData);
          setModules([]);
        }
      } catch (error) {
        console.error('Error loading modules:', error);
        setModules([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && token) {
      loadModules();
    }
  }, [currentUser, token]);

  // Load selected module data
  const handleModuleSelect = async (moduleId) => {
    if (!moduleId) {
      setSelectedModule('');
      setModuleData({
        module_code: '',
        module_name: '',
        credits: '',
        description: '',
        semester: '',
        photo_url: '',
        active: true
      });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${getApiUrl()}/modules/${moduleId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedModule(moduleId);
        setModuleData({
          module_code: data.module_code || '',
          module_name: data.module_name || '',
          credits: data.credits || '',
          description: data.description || '',
          semester: data.semester || '',
          photo_url: data.photo_url || '',
          active: data.active !== undefined ? data.active : true
        });
      } else {
        console.error('Failed to load module:', response.status);
      }
    } catch (error) {
      console.error('Error loading module data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setModuleData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedModule) return;

    try {
      setLoading(true);
      const response = await fetch(`${getApiUrl()}/modules/${selectedModule}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          moduleCode: moduleData.module_code,
          credits: moduleData.credits,
          description: moduleData.description,
          active: moduleData.active
        })
      });
      
      if (response.ok) {
        alert('Module updated successfully!');
        // Reload modules list to reflect changes
        const modulesResponse = await fetch(`${getApiUrl()}/modules`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (modulesResponse.ok) {
          const data = await modulesResponse.json();
          setModules(data || []);
        }
      } else {
        const errorData = await response.json();
        alert(`Error updating module: ${errorData.error || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error updating module:', error);
      alert('Error updating module. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{width: '100%', height: '100%', position: 'relative', background: 'white'}}>
      <Sidebar />
      
      <div style={{marginLeft: 290, width: 'calc(100% - 290px)', minHeight: '100vh'}}>
        <Header />
        
        {!currentUser || !token ? (
          <div style={{margin: '40px', textAlign: 'center', padding: '40px'}}>
            <h2 style={{color: '#F04915', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700'}}>
              Please Log In
            </h2>
            <p style={{fontSize: 16, fontFamily: 'Outfit', color: '#77838F'}}>
              You need to be logged in as an admin to edit modules.
            </p>
          </div>
        ) : (
        <>
        {/* Page Title */}
        <h1 style={{fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', marginTop: '20px', paddingLeft: '40px'}}>
          Edit Add Module
        </h1>

        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: 'white',
              padding: '20px 40px',
              borderRadius: 8,
              textAlign: 'center',
              fontSize: 16,
              fontFamily: 'Outfit',
              color: '#F04915'
            }}>
              Processing...
            </div>
          </div>
        )}

        {/* Content Area */}
        <div style={{marginTop: '20px', margin: '20px 40px'}}>
          <div style={{background: '#F04915', padding: '20px', borderTopLeftRadius: 15, borderTopRightRadius: 15}}>
            <h2 style={{color: 'white', fontSize: 24, fontFamily: 'Outfit', fontWeight: '700', margin: 0}}>
              Edit Module Information
            </h2>
            <p style={{color: 'white', fontSize: 14, fontFamily: 'Outfit', fontWeight: '400', margin: '10px 0 0 0'}}>
              Select a module to edit its details or delete it
            </p>
          </div>

          <div style={{
            background: '#FFFBF5', 
            padding: '40px',
            borderBottomLeftRadius: 15,
            borderBottomRightRadius: 15,
            minHeight: '400px'
          }}>
            
            {/* Module Selection */}
            <div style={{marginBottom: '30px'}}>
              <label style={{
                display: 'block',
                color: '#F04915',
                fontSize: 16,
                fontFamily: 'Outfit',
                fontWeight: '600',
                marginBottom: '10px'
              }}>
                Select Module to Edit:
              </label>
              <select
                value={selectedModule}
                onChange={(e) => handleModuleSelect(e.target.value)}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  height: 45,
                  padding: '0 15px',
                  border: '2px solid #E5BD9E',
                  borderRadius: 8,
                  fontSize: 16,
                  fontFamily: 'Outfit',
                  background: 'white'
                }}
              >
                <option value="">
                  {loading ? 'Loading active modules...' : 
                   modules.length === 0 ? 'No active modules found for your university' : 
                   'Choose a module...'}
                </option>
                {modules.map((module) => (
                  <option key={module.id} value={module.id}>
                    {module.module_code} - {module.module_name}
                  </option>
                ))}
              </select>
              
              {/* Debug info for development */}
              {modules.length > 0 && (
                <div style={{
                  fontSize: 12,
                  color: '#77838F',
                  marginTop: '5px'
                }}>
                  Found {modules.length} active modules for your university
                </div>
              )}
              
              {modules.length === 0 && !loading && (
                <div style={{
                  fontSize: 14,
                  color: '#DC3545',
                  marginTop: '10px',
                  padding: '10px',
                  background: 'rgba(220, 53, 69, 0.1)',
                  borderRadius: '4px'
                }}>
                  No active modules found for your university. Please add modules first using the "Add Module" feature, or check if existing modules are marked as active.
                </div>
              )}
            </div>

            {/* Edit Form */}
            {selectedModule ? (
              <form onSubmit={handleSubmit}>
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                  {/* Left Column */}
                  <div>
                    {/* Module Name - Read Only */}
                    <div style={{marginBottom: '20px'}}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: 'black',
                        fontSize: 20,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}>
                        Module Name (Read Only)
                      </label>
                      <input
                        type="text"
                        value={moduleData.module_name}
                        readOnly
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: '#f8f9fa',
                          color: '#6c757d'
                        }}
                      />
                    </div>

                    {/* Module Code - Editable */}
                    <div style={{marginBottom: '20px'}}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: 'black',
                        fontSize: 20,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}>
                        Module Code <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <input
                        type="text"
                        value={moduleData.module_code}
                        onChange={(e) => handleInputChange('module_code', e.target.value)}
                        required
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit'
                        }}
                      />
                    </div>

                    {/* Semester - Read Only */}
                    <div style={{marginBottom: '20px'}}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: 'black',
                        fontSize: 20,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}>
                        Semester (Read Only)
                      </label>
                      <input
                        type="text"
                        value={moduleData.semester || 'Not specified'}
                        readOnly
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit',
                          backgroundColor: '#f8f9fa',
                          color: '#6c757d'
                        }}
                      />
                    </div>
                  </div>

                  {/* Right Column */}
                  <div>
                    {/* Credits - Editable */}
                    <div style={{marginBottom: '20px'}}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: 'black',
                        fontSize: 20,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}>
                        Credits <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={moduleData.credits}
                        onChange={(e) => handleInputChange('credits', e.target.value)}
                        required
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit'
                        }}
                      />
                    </div>

                    {/* Status - Editable */}
                    <div style={{marginBottom: '20px'}}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: 'black',
                        fontSize: 20,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}>
                        Status <span style={{color: '#F04915'}}>*</span>
                      </label>
                      <select
                        value={moduleData.active ? 'Active' : 'Inactive'}
                        onChange={(e) => handleInputChange('active', e.target.value === 'Active')}
                        style={{
                          width: '100%',
                          height: 49,
                          padding: '0 15px',
                          borderRadius: 8,
                          border: '1px solid #77838F',
                          fontSize: 16,
                          fontFamily: 'Outfit'
                        }}
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>

                    {/* Photo - Read Only Display */}
                    <div style={{marginBottom: '20px'}}>
                      <label style={{
                        display: 'block',
                        marginBottom: '8px',
                        color: 'black',
                        fontSize: 20,
                        fontFamily: 'Outfit',
                        fontWeight: '500'
                      }}>
                        Photo (Read Only)
                      </label>
                      <div style={{
                        width: '100%',
                        height: 120,
                        border: '1px dashed #77838F',
                        borderRadius: 12,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f8f9fa'
                      }}>
                        {moduleData.photo_url ? (
                          <img
                            src={moduleData.photo_url}
                            alt="Module"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              borderRadius: 8
                            }}
                          />
                        ) : (
                          <div style={{
                            color: '#6c757d',
                            fontSize: 16,
                            fontFamily: 'Outfit',
                            textAlign: 'center'
                          }}>
                            No photo available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description - Full Width, Editable */}
                  <div style={{gridColumn: '1 / -1', marginTop: '20px'}}>
                    <label style={{
                      display: 'block',
                      marginBottom: '8px',
                      color: 'black',
                      fontSize: 20,
                      fontFamily: 'Outfit',
                      fontWeight: '500'
                    }}>
                      Description
                    </label>
                    <textarea
                      value={moduleData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '15px',
                        borderRadius: 8,
                        border: '1px solid #77838F',
                        fontSize: 16,
                        fontFamily: 'Outfit',
                        resize: 'vertical',
                        minHeight: '100px'
                      }}
                      placeholder="Enter module description..."
                    />
                  </div>
                </div>
              </form>
            ) : (
              /* No Module Selected Message */
              <div style={{
                textAlign: 'center',
                color: '#77838F',
                fontSize: 16,
                fontFamily: 'Outfit',
                fontWeight: '400',
                marginTop: '40px',
                padding: '40px',
                background: 'rgba(229, 189, 158, 0.1)',
                borderRadius: '12px'
              }}>
                <p>Please select a module from the dropdown above to edit its details.</p>
                <p style={{fontSize: '14px', marginTop: '10px', opacity: 0.7}}>
                  You can update module information or delete modules that are no longer needed.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        {selectedModule && (
          <div style={{margin: '40px', display: 'flex', justifyContent: 'space-between'}}>
            <Link to="/add-module">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back
              </button>
            </Link>
            
            <button
              type="submit"
              form="editForm"
              disabled={loading}
              style={{
                width: 269,
                padding: '11px 20px',
                background: loading ? '#ccc' : '#F04915',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontSize: 16,
                fontFamily: 'Outfit',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
              onClick={handleSubmit}
              onMouseOver={(e) => {
                if (!loading) {
                  e.target.style.background = '#D63A0F';
                }
              }}
              onMouseOut={(e) => {
                if (!loading) {
                  e.target.style.background = '#F04915';
                }
              }}
            >
              {loading ? 'Updating...' : 'Update Module'}
            </button>
          </div>
        )}

        {/* Navigation Buttons - When no module selected */}
        {!selectedModule && (
          <div style={{display: 'flex', justifyContent: 'space-between', margin: '40px'}}>
            <Link to="/add-module">
              <button 
                type="button" 
                style={{
                  width: 269, 
                  padding: '11px 20px', 
                  background: '#6C757D', 
                  border: 'none', 
                  borderRadius: 8, 
                  color: 'white', 
                  fontSize: 16, 
                  fontFamily: 'Outfit', 
                  fontWeight: '500', 
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = '#5A6268';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#6C757D';
                }}
              >
                Back to Module Management
              </button>
            </Link>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default EditModuleWorking;
